/**
 * Queue API
 *
 * Functions for managing the document queue from the web UI.
 */

import api from './index';

/**
 * Get all queued documents
 *
 * @param {Object} params - Query parameters
 * @param {string} params.status - Filter by status (comma-separated)
 * @param {string} params.source - Filter by source (api/web)
 * @param {number} params.limit - Max results
 * @param {number} params.offset - Pagination offset
 * @returns {Promise<Object>} Response with documents array
 */
export const getQueue = (params = {}) => {
  return api.get('/queue', { params });
};

/**
 * Get queue statistics
 *
 * @returns {Promise<Object>} Response with queue stats
 */
export const getQueueStats = () => {
  return api.get('/queue/stats');
};

/**
 * Get a single queue entry with details
 *
 * @param {string} id - Queue entry ID
 * @returns {Promise<Object>} Response with queue entry and signed URL
 */
export const getQueueEntry = (id) => {
  return api.get(`/queue/${id}`);
};

/**
 * Trigger AI extraction for a pending document
 *
 * @param {string} id - Queue entry ID
 * @param {string} provider - AI provider (optional)
 * @returns {Promise<Object>} Response with extracted data
 */
export const processDocument = (id, provider) => {
  return api.post(`/queue/${id}/process`, { provider });
};

/**
 * Approve a document and create a PO / OB / BL depending on document type.
 *
 * @param {string} id - Queue entry ID
 * @param {Object} body - Pre-shaped request body ({ poData }, { obData }, or { blData })
 * @returns {Promise<Object>} Response with queue entry and created record
 */
export const approveDocument = (id, body) => {
  return api.post(`/queue/${id}/approve`, body);
};

/**
 * Reject a document
 *
 * @param {string} id - Queue entry ID
 * @param {string} notes - Optional rejection notes
 * @param {boolean} deleteFile - Whether to delete the file (default true)
 * @returns {Promise<Object>} Response with rejected entry
 */
export const rejectDocument = (id, notes, deleteFile = true) => {
  return api.post(`/queue/${id}/reject`, { notes, deleteFile });
};

/**
 * Get queue entry associated with a purchase order
 *
 * @param {string} poId - Purchase order ID
 * @returns {Promise<Object>} Response with queue entry
 */
export const getQueueEntryByPO = (poId) => {
  return api.get(`/queue/by-po/${poId}`);
};

/**
 * Get queue entry associated with an ocean booking
 *
 * @param {string} obId - Ocean booking ID
 * @returns {Promise<Object>} Response with queue entry
 */
export const getQueueEntryByOB = (obId) => {
  return api.get(`/queue/by-ob/${obId}`);
};

/**
 * Get queue entry associated with an ocean bill of lading
 *
 * @param {string} blId - Ocean bill of lading ID
 * @returns {Promise<Object>} Response with queue entry
 */
export const getQueueEntryByBL = (blId) => api.get(`/queue/by-bl/${blId}`);

/**
 * Mark a queue issue as resolved
 *
 * @param {string} id - Queue entry ID
 * @param {number} issueIndex - Index of the issue in the issues array
 * @returns {Promise<Object>} Response with updated queue entry
 */
export const resolveQueueIssue = (id, issueIndex) => {
  return api.patch(`/queue/${id}/issues/${issueIndex}/resolve`);
};

/**
 * Permanently delete a queue entry
 *
 * @param {string} id - Queue entry ID
 * @returns {Promise<Object>} Response with success message
 */
export const deleteQueueEntry = (id) => {
  return api.delete(`/queue/${id}`);
};
